import React, { useRef, useState, useEffect } from 'react';
import { T, FONTS, SHADOW } from '../../constants/theme.js';
import { Btn } from '../../components/ui.jsx';
import {
  amountInWords, fmtNum, computeInvoiceTaxFromCharges, projectedTaxableCharge,
  DEFAULT_PAYMENT_REQUEST_BODY, renderTemplate,
} from '../../utils/invoiceUtils.js';
import { openPrintWindow, resolveAssetUrl } from '../../utils/printWindow.js';
import { fetchCorporateAccount } from '../../services/api.js';

/**
 * "Request for Payment" letter — modelled on the uploaded sample: a short
 * formal letter stating what the company owes, with a signature/stamp
 * block. Distinct from the itemized Proforma Invoice (InvoicePrint), this
 * is the cover letter typically attached to it for corporate billing.
 *
 * The body paragraph wording is fully configurable per property (Property
 * Setup → Invoicing & Tax) and can be further overridden per contracted
 * company (Corporate Accounts → Advanced) — see DEFAULT_PAYMENT_REQUEST_BODY
 * and renderTemplate in invoiceUtils.js for the merge-field mechanism.
 * Everything else (logo, header, address, branding, approval/signer role)
 * comes from `property`, exactly like the Invoice — nothing here is
 * hard-coded per business.
 */
export default function PaymentRequestPrint({ folio, reservation, property, onClose }) {
  const printRef = useRef();

  const currency = folio?.currency || reservation?.currency || property?.currency || 'USD';
  const confirmationNumber = reservation?.confirmationNumber || folio?.confirmationNumber || '—';
  const guestName = reservation?.guestName || folio?.guestName || '—';

  const isCorporate = reservation?.reservationCategory === 'corporate' && reservation?.corporateAccountId;
  const [corporateAccount, setCorporateAccount] = useState(null);
  useEffect(() => {
    if (!isCorporate) { setCorporateAccount(null); return; }
    fetchCorporateAccount(reservation.corporateAccountId).then(setCorporateAccount).catch(() => {});
  }, [isCorporate, reservation?.corporateAccountId]);

  const realCharges = (folio?.charges || []).filter(c => !c.voided);
  const isProjected = realCharges.length === 0;
  const taxableCharges = isProjected ? projectedTaxableCharge(reservation, property) : realCharges;
  const tax = computeInvoiceTaxFromCharges(taxableCharges, property);
  const words = amountInWords(tax.grossTotal, currency);

  const allRoom  = realCharges.length > 0 && realCharges.every(c => c.chargeType === 'room');
  const reason   = isProjected ? 'Accommodation' : allRoom ? 'Accommodation' : 'Accommodation and Consumption';

  // Resolution order: company-specific override > property default > built-in fallback.
  const bodyTemplate = corporateAccount?.paymentRequestBodyOverride
    || property?.paymentRequestBodyTemplate
    || DEFAULT_PAYMENT_REQUEST_BODY;
  const billedTo = corporateAccount?.name || guestName;
  const bodyText = renderTemplate(bodyTemplate, {
    company:        billedTo,
    property:       property?.name || 'the property',
    amountWords:    words,
    amountNumeric:  fmtNum(tax.grossTotal),
    currency,
    totalTax:       fmtNum(tax.totalTax),
    reason,
    invoiceNumber:  confirmationNumber,
  });

  const signerRole = property?.paymentRequestSignerRole || 'Manager';
  const today = new Date();
  const todayLong = today.toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' });
  const hasFooterConfig = !!(property?.tin || property?.invoiceFooterText);

  const doPrint = () => {
    const ok = openPrintWindow({
      title: `Payment Request — ${confirmationNumber}`,
      bodyHtml: printRef.current.innerHTML,
      format: 'letter',
    });
    if (!ok) alert('Your browser blocked the print popup. Please allow popups for this site and try again.');
  };

  return (
    <div>
      {!hasFooterConfig && (
        <div style={{ background:T.surface, border:`1px solid ${T.border}`, borderRadius:10,
          padding:'10px 14px', marginBottom:14 }}>
          <p style={{ fontSize:12, color:T.muted }}>
            ℹ️ This property hasn't set a TIN or bank/footer details yet. Add them in{' '}
            <strong style={{ color:T.navy }}>Property Setup → Invoicing &amp; Tax</strong> so every document includes them automatically.
          </p>
        </div>
      )}
      {!isCorporate && (
        <div style={{ background:T.warningPale, border:`1px solid ${T.warning}30`, borderRadius:10,
          padding:'10px 14px', marginBottom:14 }}>
          <p style={{ fontSize:12, color:T.warning, fontWeight:600 }}>
            ⚠️ This isn't a Corporate Reservation, so the letter is addressed to the guest directly rather than a contracted company.
          </p>
        </div>
      )}

      {/* ── Screen / print preview ── */}
      <div ref={printRef} style={{
        fontFamily:FONTS.body, color:T.navy, background:T.white,
        maxWidth:680, margin:'0 auto', padding:'34px 38px',
        border:`1px solid ${T.border}`, borderRadius:8, boxShadow:SHADOW.lg,
      }}>

        {/* Header: property info + logo, mirroring the Invoice */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:18, marginBottom:30 }}>
          <div>
            <p style={{ fontFamily:FONTS.display, fontSize:15, fontWeight:800, color:T.navy, marginBottom:3, textTransform:'uppercase' }}>
              {property?.name || 'Property Name'}
            </p>
            {property?.addressLine1 && <p style={{ fontSize:10.5, color:T.teal, lineHeight:1.5 }}>{property.addressLine1}</p>}
            {property?.addressLine2 && <p style={{ fontSize:10.5, color:T.teal, lineHeight:1.5 }}>{property.addressLine2}</p>}
            <p style={{ fontSize:10.5, color:T.teal, lineHeight:1.5 }}>
              {[property?.city, property?.country].filter(Boolean).join(', ')}
            </p>
            {property?.phone && <p style={{ fontSize:10.5, color:T.teal, lineHeight:1.5 }}>TEL: {property.phone}</p>}
            {property?.email && <p style={{ fontSize:10.5, color:T.teal, lineHeight:1.5 }}>E-mail: {property.email}</p>}
            {property?.tin && <p style={{ fontSize:10.5, color:T.teal, lineHeight:1.5 }}>TIN &amp; TVA: {property.tin}</p>}
          </div>
          {property?.logoUrl && (
            <img src={resolveAssetUrl(property.logoUrl)} alt="" crossOrigin="anonymous"
              style={{ maxWidth:110, maxHeight:80, objectFit:'contain' }} />
          )}
        </div>

        {/* Title */}
        <div style={{ textAlign:'center', marginBottom:26 }}>
          <p style={{
            fontFamily:FONTS.display, fontSize:14, fontWeight:800, color:T.navy,
            textDecoration:'underline', letterSpacing:1.5, textTransform:'uppercase',
          }}>
            Request for Payment
          </p>
        </div>

        {/* Body paragraph — fully configurable per property / per company */}
        <p style={{ fontSize:12.5, color:T.navy, lineHeight:1.9, marginBottom:22, textAlign:'justify' }}>
          {bodyText}
        </p>

        <p style={{ fontSize:12.5, color:T.navy, lineHeight:1.9, marginBottom:34 }}>
          We say the sum of <strong>{words} ({fmtNum(tax.grossTotal)} {currency}).</strong>
        </p>

        {/* Done at / authorized by / signature */}
        <div style={{ display:'flex', justifyContent:'flex-end' }}>
          <div style={{ minWidth:260 }}>
            <p style={{ fontSize:12, color:T.slate, marginBottom:8 }}>
              Done at {property?.city || property?.name || '—'}: {todayLong}
            </p>
            <p style={{ fontSize:12, color:T.slate, marginBottom:6 }}>
              Authorized by: <span style={{ display:'inline-block', minWidth:160, borderBottom:`1px solid ${T.border}` }}>&nbsp;</span>
            </p>
            <p style={{ fontSize:12, color:T.slate, marginBottom:30 }}>Function: {signerRole}</p>
            <p style={{ fontSize:11, color:T.muted }}>Signature &amp; Stamp</p>
          </div>
        </div>

        {/* Bank / TIN footer — same configured text used on the Invoice */}
        {property?.invoiceFooterText && (
          <div style={{ borderTop:`2px solid ${T.navy}`, paddingTop:12, marginTop:36 }}>
            <p style={{ fontSize:10, fontWeight:700, color:T.navy, whiteSpace:'pre-wrap', lineHeight:1.7 }}>
              {property.invoiceFooterText}
            </p>
          </div>
        )}
      </div>{/* /printRef */}

      <div style={{ display:'flex', gap:10, marginTop:20, maxWidth:680, margin:'20px auto 0' }}>
        <Btn variant="secondary" onClick={onClose} style={{ flex:1 }}>Close</Btn>
        <Btn variant="primary" onClick={doPrint} style={{ flex:2 }}>🖨️ Print Payment Request</Btn>
      </div>
    </div>
  );
}
